import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  DollarSign,
  Zap,
  TrendingUp,
  AlertCircle,
  User
} from 'lucide-react';
import { getAdminAIUsageStats, getAdminAIUsageUsers } from '../../services/api';
import './AIUsage.css';

const AIUsage = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAIUsageData();
  }, []);

  const fetchAIUsageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch stats using API service
      const statsResponse = await getAdminAIUsageStats();
      console.log('API Response:', statsResponse.data);
      console.log('Stats Data:', statsResponse.data.data);
      console.log('Overall Stats:', statsResponse.data.data?.overall);
      setStats(statsResponse.data.data?.overall || statsResponse.data.data);

      // Fetch user usage using API service
      const usersResponse = await getAdminAIUsageUsers();
      setUsers(usersResponse.data.data || []);

    } catch (err) {
      console.error('Error fetching AI usage data:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load AI usage data');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading AI usage analytics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <AlertCircle size={48} />
        <h2>Error Loading Data</h2>
        <p>{error}</p>
        <button className="btn-retry" onClick={fetchAIUsageData}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="ai-usage">
      {/* Header */}
      <div className="ai-usage-header">
        <div>
          <h1>AI Usage Analytics</h1>
          <p>Monitor AI API usage, costs, and user activity</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon stat-icon-primary">
            <Zap size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Requests</h3>
            <p className="stat-value">{formatNumber(stats?.totalRequests)}</p>
            <span className="stat-label">API calls made</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-success">
            <BarChart3 size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Tokens Used</h3>
            <p className="stat-value">{formatNumber(stats?.totalTokens)}</p>
            <span className="stat-label">Tokens consumed</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon stat-icon-warning">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <h3>Total Cost</h3>
            <p className="stat-value">{formatCurrency(stats?.totalCost)}</p>
            <span className="stat-label">USD spent</span>
          </div>
        </div>
      </div>

      {/* Model Breakdown */}
      {stats?.modelBreakdown && stats.modelBreakdown.length > 0 && (
        <div className="section-card">
          <div className="section-header">
            <h2>Model Breakdown</h2>
            <p>Usage and cost by AI model</p>
          </div>
          <div className="model-breakdown">
            {stats.modelBreakdown.map((model, index) => (
              <div key={index} className="model-card">
                <div className="model-header">
                  <TrendingUp size={20} className="model-icon" />
                  <h3>{model.model || 'Unknown Model'}</h3>
                </div>
                <div className="model-stats">
                  <div className="model-stat">
                    <span className="model-stat-label">Requests</span>
                    <span className="model-stat-value">{formatNumber(model.requests)}</span>
                  </div>
                  <div className="model-stat">
                    <span className="model-stat-label">Tokens</span>
                    <span className="model-stat-value">{formatNumber(model.tokens)}</span>
                  </div>
                  <div className="model-stat">
                    <span className="model-stat-label">Cost</span>
                    <span className="model-stat-value">{formatCurrency(model.cost)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Usage Table */}
      <div className="section-card">
        <div className="section-header">
          <h2>User Usage</h2>
          <p>AI usage breakdown by user</p>
        </div>

        {users.length === 0 ? (
          <div className="empty-state">
            <User size={64} strokeWidth={1.5} />
            <h3>No user data available</h3>
            <p>AI usage data will appear here once users start using AI features</p>
          </div>
        ) : (
          <div className="users-table">
            <table>
              <thead>
                <tr>
                  <th>User Name</th>
                  <th>Email</th>
                  <th>Requests</th>
                  <th>Tokens</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={index}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">
                          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <span className="user-name">{user.name || 'Unknown User'}</span>
                      </div>
                    </td>
                    <td>
                      <span className="user-email">{user.email || 'N/A'}</span>
                    </td>
                    <td>
                      <span className="table-value">{formatNumber(user.requests)}</span>
                    </td>
                    <td>
                      <span className="table-value">{formatNumber(user.tokens)}</span>
                    </td>
                    <td>
                      <span className="table-value table-value-cost">{formatCurrency(user.cost)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIUsage;
